import * as React from 'react';
import locales from "../locales";
import {YearMonth, YearMonthRangePicker} from "./utils/YearMonthRangePicker";
import {SelectValue} from "antd/lib/select";
import {CategoryRangeStatistics, TotalCategoryCostStatistics,} from "../api/dtos/statistics";
import {
  renderAlertStatLayoutItem,
  renderCostStatistics,
  renderLegendDot,
  renderPercentTextIfPossible,
  renderPriceStatLayoutItem,
  renderStatHeader,
  renderStatLayout,
  renderStatLayoutItem,
  renderTitledStatLayout,
  renderUnitStatLayoutItem
} from "./utils/statistics-utils";
import SectionHeaderFormItem from "../common/form-items/SectionHeaderFormItem";
import {ChartData, RentimeChart} from "./RentimeChart";
import {getColor} from "./utils/statistics-colors";
import {groupNumber} from "../utils/number-utils";
import StaticFormItem from "../common/form-items/StaticFormItem";
import AutoCompleteInput from "../common/input/AutoCompleteInput";

interface Props {
  totalCostStats?: TotalCategoryCostStatistics;
  categories: string[];
  onCategoryChange: (category: string) => void;
  onQueryParamsChange: (category: string, start: YearMonth, end: YearMonth) => void;
  categoryStats?: CategoryRangeStatistics;
}

interface State {
  categoryDataSource: string[],
  start?: YearMonth;
  end?: YearMonth;
  category?: string;
}

export default class CategoryStatisticsPanel extends React.Component<Props, State> {
  public state: State = {
    categoryDataSource: this.props.categories,
  };

  public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
    if (prevProps.categories !== this.props.categories) {
      this.state.categoryDataSource = this.props.categories;
    }
  }

  public render(): React.ReactNode {
    const {totalCostStats, categoryStats} = this.props;
    const {categoryDataSource} = this.state;

    return (
      <>
        <StaticFormItem label={locales.category} initialValue={
          <AutoCompleteInput
            className={"mild-disabled"}
            onChange={this.onCategorySelect}
            value={this.state.category}
            dataSource={categoryDataSource}
          />
        }/>
        {totalCostStats && this.renderTotalCostStats((totalCostStats))}
        <SectionHeaderFormItem title={locales.range}/>
        <YearMonthRangePicker
          onRangePick={this.onRangePick}
          onRangeClear={this.onRangeClear}
        />
        {categoryStats && renderCostStatistics(categoryStats.costStatistics)}
        {categoryStats && this.renderUsageStatistics(categoryStats)}
      </>
    );
  }

  private renderTotalCostStats = (stats: TotalCategoryCostStatistics): React.ReactNode => {
    return renderTitledStatLayout(
      locales.total_income,
      <>
        {renderUnitStatLayoutItem(locales.items, stats.itemCount, locales.pcs)}
        {renderPriceStatLayoutItem(locales.initial_cost, stats.initialCost)}
        {renderPriceStatLayoutItem(locales.total_maintenance_cost, stats.totalMaintenanceCost)}
        {renderPriceStatLayoutItem(locales.internal_usage_cost, stats.internalUsageCost)}
        {renderPriceStatLayoutItem(locales.total_rental_income, stats.totalRentalIncome)}
        {renderPriceStatLayoutItem(locales.total_maintenance_income, stats.totalMaintenanceIncome)}
        {renderPriceStatLayoutItem(locales.profit, stats.profit)}
      </>
    );
  };

  private renderUsageStatistics = (stats: CategoryRangeStatistics): React.ReactNode => {
    const chartData: ChartData = this.createChartData(stats);
    const {days, daysRented, daysUnused} = stats.usageStatistics;
    return (
      <>
        {renderStatHeader(locales.usage)}
        {renderStatLayout(
          <>
            <b>{renderUnitStatLayoutItem(locales.total, days, locales.day)}</b>
            {this.renderUsageStatItem(locales.state_rented, daysRented, days, stats.itemCount, getColor(0))}
            {this.renderUsageStatItem(locales.not_used, daysUnused, days, stats.itemCount, getColor(1))}
            {renderAlertStatLayoutItem(locales.usage_statistics_notes)}
          </>,
          <RentimeChart unit={locales.day} data={chartData}/>
        )}
      </>
    );
  };

  private renderUsageStatItem = (label: string, value: number, total: number, itemCount: number, color?: string): React.ReactNode => {
    return renderStatLayoutItem(
      label,
      <>
        {color && renderLegendDot(color)}
        {groupNumber(value)} {locales.day}
        {renderPercentTextIfPossible(value, total)}
      </>
    );
  };

  private onCategorySelect = (selected: SelectValue) => {
    this.setState({category: String(selected)}, () => {
      this.triggerQueryCallbackIfPossible();
    });
  };

  private onRangeClear = () => {
    this.setState({
      start: undefined,
      end: undefined,
    });
  };

  private onRangePick = (start: YearMonth | undefined, end: YearMonth | undefined) => {
    this.setState({start, end}, () => {
      this.triggerQueryCallbackIfPossible();
    });
  };

  private triggerQueryCallbackIfPossible() {
    const {categoryDataSource, category, start, end} = this.state;

    if (category && categoryDataSource.includes(category)) {
      if (start && end) {
        this.props.onQueryParamsChange(category, start, end);
      }
      this.props.onCategoryChange(category);
    }
  }

  private createChartData = (stats: CategoryRangeStatistics): ChartData => {
    const avg = (value: number, total: number) => value / total;
    const {daysRented, daysUnused} = stats.usageStatistics;
    return [{
      name: 'Kiadva', value: daysRented, avgValue: avg(daysRented, stats.itemCount)
    }, {
      name: 'Nem használt', value: daysUnused, avgValue: avg(daysUnused, stats.itemCount)
    }];
  };
}



// WEBPACK FOOTER //
// ./src/statistics/CategoryStatisticsPanel.tsx